import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const FILE_UPLOAD_STATUS_VALUES = ['pending', 'completed', 'aborted', 'failed'] as const
export const fileUploadStatusSchema = z.enum(FILE_UPLOAD_STATUS_VALUES)
export type FileUploadStatus = z.infer<typeof fileUploadStatusSchema>

/**
 * Extensions the platform will ever accept, before a per-field/competition
 * allowlist narrows this further. `.exe/.js/.sh/.bat/.ps1` and friends are
 * never in this list — see `ref/07-phase5-6-submission-r2.md`'s file type
 * security section.
 */
export const ALLOWED_FILE_EXTENSIONS = [
  'pdf',
  'zip',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
  'txt',
  'csv',
  'mp4',
  'mov',
] as const

/**
 * The extension/Content-Type pairs the file-type check requires a match on —
 * every extension in `ALLOWED_FILE_EXTENSIONS` has an entry, several
 * legitimately accepting more than one MIME type (browsers and OSes disagree
 * on e.g. `.zip`'s exact Content-Type).
 */
export const REQUIRED_CONTENT_TYPE_BY_EXTENSION: Record<string, readonly string[]> = {
  pdf: ['application/pdf'],
  zip: ['application/zip', 'application/x-zip-compressed', 'application/octet-stream'],
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  gif: ['image/gif'],
  webp: ['image/webp'],
  doc: ['application/msword'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ppt: ['application/vnd.ms-powerpoint'],
  pptx: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  xls: ['application/vnd.ms-excel'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  txt: ['text/plain'],
  csv: ['text/csv', 'application/vnd.ms-excel'],
  mp4: ['video/mp4'],
  mov: ['video/quicktime'],
}

/** Never permitted, whatever a competition's allowlist says. */
export const ALWAYS_BLOCKED_EXTENSIONS = [
  'exe',
  'js',
  'mjs',
  'cjs',
  'sh',
  'bat',
  'ps1',
  'cmd',
] as const

/**
 * Which extensions a `file`/`image`/`pdf`/`zip` field (see
 * `packages/schemas/src/form.ts`'s `FILE_FIELD_TYPES`) will accept. `file` is
 * the generic slot and accepts anything in `ALLOWED_FILE_EXTENSIONS`; the
 * other three narrow it to what their name promises.
 */
export const FILE_FIELD_TYPE_ALLOWED_EXTENSIONS: Record<
  'file' | 'image' | 'pdf' | 'zip',
  readonly string[]
> = {
  file: ALLOWED_FILE_EXTENSIONS,
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp'],
  pdf: ['pdf'],
  zip: ['zip'],
}

// ---------------------------------------------------------------------------
// Submission file
// ---------------------------------------------------------------------------

export const submissionFileSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  submissionId: publicIdSchema,
  submissionVersionId: publicIdSchema.nullable(),
  /** Non-null only for a mutable draft artifact owned by the multipart DO. */
  multipartUploadId: z.string().min(1).nullable(),
  formFieldId: publicIdSchema,
  r2Key: z.string().min(1),
  originalFilename: z.string().min(1),
  displayFilename: z.string().min(1),
  contentType: z.string().min(1),
  extension: z.string().min(1).max(16),
  sizeBytes: z.int().min(0),
  etag: z.string().nullable(),
  checksumSha256: z.string().nullable(),
  uploadStatus: fileUploadStatusSchema,
  /** Present for mutable staging rows; null for immutable version snapshots. */
  uploadExpiresAt: utcTimestampSchema.nullable(),
  uploadedBy: publicIdSchema,
  createdAt: utcTimestampSchema,
  deletedAt: utcTimestampSchema.nullable(),
})
export type SubmissionFile = z.infer<typeof submissionFileSchema>

/** Never carries `r2Key` — that is an internal storage detail, not something a client needs. */
export const submissionFileDtoSchema = submissionFileSchema.omit({
  competitionId: true,
  submissionId: true,
  r2Key: true,
  multipartUploadId: true,
  uploadExpiresAt: true,
})
export type SubmissionFileDto = z.infer<typeof submissionFileDtoSchema>

/**
 * Admin/staff cross-submission list view — carries back the submission +
 * team context a bare file row can't, since the admin Files page lists
 * across the whole competition rather than one submission at a time.
 */
export const adminSubmissionFileDtoSchema = submissionFileDtoSchema.extend({
  submissionId: publicIdSchema,
  teamId: publicIdSchema,
  teamName: z.string(),
})
export type AdminSubmissionFileDto = z.infer<typeof adminSubmissionFileDtoSchema>

/**
 * PR-148/AC-153. Submission-detail view of a file — adds the uploader's
 * display name (resolved from `users.display_name`, never a raw id) so a
 * competition admin can see who on the team uploaded it, without carrying
 * the team/submission context `AdminSubmissionFileDto` needs for its
 * cross-submission list (the detail view already knows both from the
 * submission itself).
 */
export const submissionFileWithUploaderDtoSchema = submissionFileDtoSchema.extend({
  uploadedByName: z.string(),
})
export type SubmissionFileWithUploaderDto = z.infer<typeof submissionFileWithUploaderDtoSchema>

/**
 * PR-148/AC-154. Which `content-disposition` a file's stream response may be
 * served with. `inline` renders in the browser (PDF/image preview); it is
 * only ever honored server-side for content types in
 * `INLINE_SAFE_CONTENT_TYPES` — anything else silently falls back to
 * `attachment` even if requested, since inline-rendering an arbitrary
 * uploaded file (e.g. HTML/SVG) would let it execute script in the app's
 * origin.
 */
export const FILE_DISPOSITION_VALUES = ['attachment', 'inline'] as const
export const fileDispositionSchema = z.enum(FILE_DISPOSITION_VALUES)
export type FileDisposition = z.infer<typeof fileDispositionSchema>

/** Content types `disposition=inline` is honored for — see `fileDispositionSchema`. */
export const INLINE_SAFE_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const

/** Query params for the admin files list — offset-paginated, same shape as `submissionListQuerySchema`. */
export const fileListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  uploadStatus: fileUploadStatusSchema.optional(),
  submissionId: publicIdSchema.optional(),
})
export type FileListQuery = z.infer<typeof fileListQuerySchema>

export const initUploadRequestSchema = z.strictObject({
  formFieldId: publicIdSchema,
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(255),
  sizeBytes: z.int().min(1),
  checksumSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/i)
    .nullable()
    .default(null),
})
export type InitUploadRequest = z.infer<typeof initUploadRequestSchema>

export const completeUploadRequestSchema = z.strictObject({
  etag: z.string().min(1).nullable().default(null),
  checksumSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/i)
    .nullable()
    .default(null),
})
export type CompleteUploadRequest = z.infer<typeof completeUploadRequestSchema>

/**
 * PR-276/UX-296/BR-217. The client's own report that an in-progress upload
 * genuinely failed (an R2 PUT rejection, a network error, a CORS block) —
 * distinct from `/abort`, which is a caller-initiated cancel. `reason` is an
 * optional short diagnostic string surfaced only in the audit log, never
 * shown to any other participant.
 */
export const reportUploadFailureRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(500).nullable().default(null),
})
export type ReportUploadFailureRequest = z.infer<typeof reportUploadFailureRequestSchema>

// ---------------------------------------------------------------------------
// Multipart upload (>100MB — see `ref/07-phase5-6-submission-r2.md`)
// ---------------------------------------------------------------------------

export const multipartInitRequestSchema = z.strictObject({
  formFieldId: publicIdSchema,
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(255),
  sizeBytes: z.int().min(1),
  checksumSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/i)
    .nullable()
    .default(null),
})
export type MultipartInitRequest = z.infer<typeof multipartInitRequestSchema>

export const multipartUploadedPartSchema = z.object({
  partNumber: z.int().min(1),
  etag: z.string().min(1),
})
export type MultipartUploadedPart = z.infer<typeof multipartUploadedPartSchema>

export const multipartCompleteRequestSchema = z.strictObject({
  parts: z.array(multipartUploadedPartSchema).min(1),
})
export type MultipartCompleteRequest = z.infer<typeof multipartCompleteRequestSchema>

export const multipartPartInitRequestSchema = z.strictObject({
  partNumber: z.int().min(1).max(10_000),
})

export const multipartPartCompleteRequestSchema = z.strictObject({
  partNumber: z.int().min(1).max(10_000),
  operationId: publicIdSchema,
  etag: z.string().min(1),
})
